import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar, Nav, Container, Button, Dropdown, Form, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaMapMarkerAlt, FaPlusCircle, FaSearch, FaHeart, FaEnvelope, FaUserCircle, FaAd, FaSignOutAlt, FaTimes, FaFileContract, FaShieldAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase';
import './Navbar.css';
import { validateSearchQuery, sanitizeInput } from '../utils/security';

const NavbarComponent = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location, setLocation] = useState(() => {
    // Try to get location from localStorage, default to 'India' if not found
    return localStorage.getItem('selectedLocation') || 'India';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [locationSearch, setLocationSearch] = useState('');
  const [filteredLocations, setFilteredLocations] = useState([]);
  
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Update localStorage when location changes
  useEffect(() => {
    localStorage.setItem('selectedLocation', location);
  }, [location]);

  const getUserInitial = (user) => {
    if (!user) return '';
    const name = user.displayName || user.email;
    return name.charAt(0).toUpperCase();
  };

  const locations = [
    'Andhra Pradesh',
    'Telangana',
    'Karnataka',
    'Tamil Nadu',
    'Kerala',
    'Maharashtra',
    'Delhi',
    'Uttar Pradesh',
    'West Bengal',
    'Gujarat',
    'Rajasthan',
    'Madhya Pradesh',
    'Punjab',
    'Haryana',
    'Bihar',
    'Odisha',
    'Assam',
    'Jharkhand',
    'Chhattisgarh'
  ];

  useEffect(() => {
    if (locationSearch) {
      const filtered = locations.filter(loc => 
        loc.toLowerCase().includes(locationSearch.toLowerCase())
      );
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations(locations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch]);

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
    setLocationSearch('');
    localStorage.setItem('selectedLocation', selectedLocation);
    // Navigate to search with the selected state
    navigate(`/search?state=${encodeURIComponent(selectedLocation)}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Validate and sanitize search query
      const searchValidation = validateSearchQuery(searchQuery.trim());
      if (!searchValidation.valid) {
        toast.error(searchValidation.error);
        return;
      }
      
      // Use sanitized query
      const cleanedQuery = searchValidation.sanitized.toLowerCase();
      console.log('Search query:', cleanedQuery);
      console.log('Location:', location);
      
      // Construct the search URL with all necessary parameters
      const searchParams = new URLSearchParams();
      searchParams.append('query', cleanedQuery); // SearchResults.js will convert 'query' to 'search' for backend
      
      // Only add location if it's not "Current Location"
      if (location !== 'Current Location') {
        searchParams.append('state', location);
      }
      
      const searchUrl = `/search?${searchParams.toString()}`;
      console.log('Navigating to:', searchUrl);
      
      // Navigate to search results
      navigate(searchUrl);
      
      // Clear the search input after navigation
      setSearchQuery('');
    } else {
      toast.warning('Please enter a search term');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setUnreadCount(0);
      return;
    }
    const userChatsRef = ref(database, `user_chats/${currentUser.uid}`);
    const unsubscribe = onValue(userChatsRef, (snapshot) => {
      let totalUnread = 0;
      if (snapshot.exists()) {
        const chatsData = snapshot.val();
        Object.values(chatsData).forEach(chat => {
          if (chat.unreadCount) totalUnread += chat.unreadCount;
        });
      }
      setUnreadCount(totalUnread);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('Current location obtained:', latitude, longitude);
          // Set location to "Current Location" temporarily
          setLocation('Current Location');
          localStorage.setItem('selectedLocation', 'Current Location');
          // Navigate to search results page with coordinates and radius
          navigate(`/search?lat=${latitude}&lng=${longitude}&radius=10`);
          toast.success('Fetching ads for your current location!');
        },
        (error) => {
          console.error('Error getting location:', error);
          let errorMessage = 'Failed to get your location.';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Please allow location access to use this feature.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'The request to get user location timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting location.';
              break;
          }
          toast.error(errorMessage);
          // Reset to India if location access fails
          setLocation('India');
          localStorage.setItem('selectedLocation', 'India');
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser.');
      // Reset to India if geolocation is not supported
      setLocation('India');
      localStorage.setItem('selectedLocation', 'India');
    }
  };

  return (
    <Navbar expand="lg" className="navbar-custom">
      <Container>
        {/* Logo */}
        <Navbar.Brand as={Link} to="/">
          <img src={require('../assets/ChatGPT Image May 17, 2025, 07_40_29 AM.png')} alt="FarmX Logo" className="navbar-logo" />
        </Navbar.Brand>
        
        {/* Mobile Menu Toggle */}
        <Navbar.Toggle 
          aria-controls="basic-navbar-nav" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        />

        <Navbar.Collapse id="basic-navbar-nav">
          {/* Location Dropdown */}
          <Dropdown className="me-2">
            <Dropdown.Toggle variant="outline-secondary" id="location-dropdown">
              <FaMapMarkerAlt className="me-1" /> {location}
            </Dropdown.Toggle>

            <Dropdown.Menu className="location-dropdown-menu">
              <div className="p-2">
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search location..."
                    value={locationSearch}
                    onChange={(e) => {
                      // Sanitize location search input
                      setLocationSearch(sanitizeInput(e.target.value));
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Button variant="outline-secondary" onClick={() => setLocationSearch('')}>
                    <FaTimes />
                  </Button>
                </InputGroup>
              </div>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={handleUseCurrentLocation}
                className="location-item"
              >
                <FaMapMarkerAlt className="me-2 text-muted" />
                Use current location
              </Dropdown.Item>
              <Dropdown.Divider />
              <div className="location-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {filteredLocations.map((loc) => (
                  <Dropdown.Item 
                    key={loc} 
                    onClick={() => handleLocationSelect(loc)}
                    className="location-item"
                  >
                    <FaMapMarkerAlt className="me-2 text-muted" />
                    {loc}
                  </Dropdown.Item>
                ))}
              </div>
            </Dropdown.Menu>
          </Dropdown>

          {/* Search Form */}
          <Form onSubmit={handleSearch} className="d-flex flex-grow-1 me-2">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="What are you looking for?"
                value={searchQuery}
                onChange={(e) => {
                  // Allow typing, validation happens on submit
                  setSearchQuery(e.target.value);
                }}
                className="search-input"
              />
              <Button type="submit" variant="outline-success">
                <FaSearch />
              </Button>
            </InputGroup>
          </Form>

          {/* Navigation Links */}
          <Nav className="ms-auto align-items-center">
            {currentUser ? (
              <>
                <Nav.Link as={Link} to="/post-ad" className="post-ad-button">
                  <Button variant="outline-success">
                    <FaPlusCircle className="me-1" /> Post Ad
                  </Button>
                </Nav.Link>

                <Nav.Link as={Link} to="/chats" className="message-icon">
                  <FaEnvelope size={22} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">
                      {unreadCount}
                    </span>
                  )}
                </Nav.Link>

                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" id="user-dropdown" className="user-dropdown-toggle">
                    <div className="user-initial">{getUserInitial(currentUser)}</div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="user-dropdown-menu">
                    <Dropdown.Item as={Link} to="/profile" className="d-flex align-items-center">
                      <FaUserCircle className="me-2" />
                      My Profile
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/my-ads" className="d-flex align-items-center">
                      <FaAd className="me-2" />
                      My Ads
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/favourites" className="d-flex align-items-center">
                      <FaHeart className="me-2" color="red" />
                      Favourites
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item as={Link} to="/terms-and-conditions" className="d-flex align-items-center">
                      <FaFileContract className="me-2" />
                      Terms of Service
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/privacy-policy" className="d-flex align-items-center">
                      <FaShieldAlt className="me-2" />
                      Privacy Policy
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleLogout} className="d-flex align-items-center text-danger">
                      <FaSignOutAlt className="me-2" />
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </>
            ) : (
              <Nav.Link as={Link} to="/login">
                Login
              </Nav.Link>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavbarComponent;